// @givmo/app-admin — Employer gift-cards decode/build helpers (EP-WEB4). ★ MONEY-MOVING ★
//
// Pure, side-effect-free helpers for the employer-gifting flow — a company sending a company-funded
// gift card to a roster employee via `POST /admin/company/gift-cards` (EP-BE2, #1302). Kept as their
// own no-React module so the amount validator, the `CompanyGiftCardSend` body construction, the
// sent-list decode, the receipt-copy fork, and the error classification are all unit-testable in
// isolation (matching the `give-decode.ts` / `payment-methods-decode.ts` split).
//
// Numbers-are-the-law: the gift face amount crosses the wire as a decimal STRING and is only formatted
// or compared as integer cents via string math — never parsed to a float or re-rounded. Tolerant
// reader: the sent-list decode reads the `{ data: [...] }` envelope and degrades to `[]`, never throws.
//
// Backend contract (`CompanyGiftCardSend`): `amount` ∈ [$15, $1,000] (CA Civ. Code §1749.5(b) floors),
// `funding_source` = "company_card" (charge a saved company card) | "company_wallet" (debit the
// company wallet), `recipient_work_email` a roster employee (the backend 422s an off-roster address),
// optional `recipient_name`/`recipient_message`, and `purchase_request_id` (the idempotency key, also
// carried in the `Idempotency-Key` header). The recipient never gets a receipt.

import {
  type CompanyGiftCardOut,
  type CompanyGiftCardSend,
  type GiftCardFunding,
  type GivmoError,
  type PaymentMethod,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { parseCentsAmount, formatCentsUsd } from '../dashboard/dashboard-decode';
import type { StatedGiftCardCharge } from './gift-card-quote';
import { cardLabel, type PaymentCard } from '../settings/payment-methods-decode';

// Re-export the shared wallet helpers so the gift-cards feature has ONE import surface for its funding
// semantics (identical to the give flow — a company wallet debit gated by the same sufficiency check),
// and the summary's "From" wording, so a confirm names its source exactly as the Give dialog does.
export {
  fundingSummaryLabel,
  toWalletFunds,
  walletCoversAmount,
  type WalletFunds,
} from '../give/give-decode';

// ── Amount floors (CA Civ. Code §1749.5(b) — mirrored from the backend `CompanyGiftCardSend`) ───────

/** The inclusive whole-dollar floor / ceiling a company gift card must fall within. */
export const GIFT_CARD_MIN_DOLLARS = 15;
export const GIFT_CARD_MAX_DOLLARS = 1000;

/** Suggested preset denominations (all within the floors), mirroring the donate `/gift-card` UX. */
export const GIFT_CARD_PRESETS: readonly number[] = [25, 50, 100, 250];

/**
 * Parse the amount input to the WHOLE-DOLLAR integer the gift card is worth, or `null` when it is not
 * a valid, in-range whole-dollar amount. STRICT: only digits (no cents, no sign, no exponent) survive,
 * and the value must fall within [$15, $1,000] — so a fractional/blank/out-of-range input keeps the
 * flow inert rather than sending a value the backend would 422. We NEVER send a number different from
 * the one shown, and never a value that would be rejected at the floor.
 */
export function parseGiftCardAmount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0 || !/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < GIFT_CARD_MIN_DOLLARS || n > GIFT_CARD_MAX_DOLLARS) return null;
  return n;
}

/** Whole-dollar USD label for a preset/amount ("$50"). */
export function formatGiftCardDollars(amount: number): string {
  return formatCentsUsd(amount * 100);
}

/** The human range label for the amount-field helper/error copy ("$15–$1,000"). */
export const GIFT_CARD_RANGE_LABEL = `${formatGiftCardDollars(GIFT_CARD_MIN_DOLLARS)}–${formatGiftCardDollars(
  GIFT_CARD_MAX_DOLLARS,
)}`;

// ── Recipient (roster employee) ─────────────────────────────────────────────────────────────────

/** A roster employee reduced to what the picker renders + the send body needs. Only employees WITH a
 *  work email are selectable (the backend addresses delivery by `work_email`). */
export interface GiftCardRecipient {
  /** The employee id (list/dedupe key; NOT sent — the backend keys on `work_email`). */
  id: string;
  /** Display name (falls back to the work email when name parts are blank). */
  name: string;
  /** The work email — the delivery address + the `recipient_work_email` body field. */
  workEmail: string;
}

// ── Funding ─────────────────────────────────────────────────────────────────────────────────────

/** The chosen funding source: a specific saved company card, or the company wallet. */
export type Funding = { kind: 'card'; cardId: string } | { kind: 'wallet' };

/** The wire `funding_source` for a funding choice. */
export function fundingSource(funding: Funding): 'company_card' | 'company_wallet' {
  return funding.kind === 'card' ? 'company_card' : 'company_wallet';
}

/**
 * The human-readable funding label restated in the confirm copy. TOLERANT of a vanished card: if the
 * selected card is no longer in the list (deleted in another tab, a refetch emptied the list), fall
 * back to a generic label rather than throwing mid-render — the backend rejects a charge against a
 * gone card anyway, so the label is display-only.
 */
export function fundingDisplayLabel(
  funding: Funding | null,
  cards: PaymentCard[],
  walletDisplay: string,
): string {
  if (funding === null) return '';
  if (funding.kind === 'wallet') return `your company wallet (${walletDisplay})`;
  const card = cards.find((c) => c.id === funding.cardId);
  return card !== undefined ? `your ${cardLabel(card)}` : 'your saved company card';
}

// ── Send body ────────────────────────────────────────────────────────────────────────────────────

/** Trim a value and return it only when non-empty — else `undefined` (so the field is omitted). */
function presentOrUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/** The `payment_method` value for a saved company-card charge (the CREDIT_CARD default). */
export const CARD_PAYMENT_METHOD: PaymentMethod = 'Credit Card';

/**
 * The single send's body: the generated `CompanyGiftCardSend`, plus the stated price a CARD send
 * carries (GC-FU31, `expected_amount_charged` / `expected_fee`) — declared here ahead of a snapshot
 * regen (`agent_docs/api-client.md` § "Additive request fields ahead of a regen"); the regen absorbs it.
 */
export type CompanyGiftCardSendBody = CompanyGiftCardSend & Partial<StatedGiftCardCharge>;

/**
 * Build the `CompanyGiftCardSend` body. `amount` is the whole-dollar integer (validated in range by the
 * caller) sent as a decimal STRING (money = string on the wire — never a float). A CARD gift carries
 * `funding_source:"company_card"` + the `card_id` + `payment_method:"Credit Card"`; a WALLET gift
 * carries `funding_source:"company_wallet"` only (card_id/payment_method are ignored server-side). The
 * SAME stable `purchase_request_id` is the idempotency key (also sent in the `Idempotency-Key` header),
 * so a retried send de-dupes and never double-charges/double-debits. Recipient name/message are OMITTED
 * (not null) when blank.
 */
export function buildCompanyGiftCardBody(params: {
  amount: number;
  funding: Funding;
  recipientWorkEmail: string;
  recipientName?: string | null;
  recipientMessage?: string | null;
  purchaseRequestId: string;
  /** A CARD send's stated price (GC-FU31), from the server's quote; `null`/absent on the wallet. */
  stated?: StatedGiftCardCharge | null;
}): CompanyGiftCardSendBody {
  const body: CompanyGiftCardSendBody = {
    amount: String(params.amount),
    funding_source: fundingSource(params.funding),
    recipient_work_email: params.recipientWorkEmail,
    purchase_request_id: params.purchaseRequestId,
  };

  if (params.funding.kind === 'card') {
    body.card_id = params.funding.cardId;
    body.payment_method = CARD_PAYMENT_METHOD;
    if (params.stated) Object.assign(body, params.stated);
  }

  const name = presentOrUndefined(params.recipientName);
  const message = presentOrUndefined(params.recipientMessage);
  if (name !== undefined) body.recipient_name = name;
  if (message !== undefined) body.recipient_message = message;

  return body;
}

// ── Receipt copy fork (plan §3 — LAW) ──────────────────────────────────────────────────────────────
//
// Company CARD ⇒ the purchase is a NEW deductible donation to GCF, so a neutral receipt line is shown
// (no deductibility language — counsel-gated). Company WALLET ⇒ the wallet was already receipted at
// DEPOSIT, so the success copy must NOT mention a receipt at all. NEVER any recipient-receipt copy.

/** The success-state receipt line for the funding source, or null (wallet → no receipt mention). */
export function giftCardReceiptCopy(funding: Funding): string | null {
  return funding.kind === 'card' ? 'A tax receipt will be emailed for this purchase.' : null;
}

// ── Sent-list decode ─────────────────────────────────────────────────────────────────────────────

/** Tolerant `{ data: [...] }` decode → the company's sent gift cards. Never throws (→ `[]`). */
export function extractCompanyGiftCards(body: unknown): CompanyGiftCardOut[] {
  return extractList<CompanyGiftCardOut>(body);
}

/** Format the wire face `amount` (a decimal string, e.g. "50" / "50.00") for the sent-list — exact
 *  cents via string math, never a float. "—" when blank/unparseable. */
export function formatGiftCardAmount(amount: string | null | undefined): string {
  return formatCentsUsd(parseCentsAmount(amount ?? ''));
}

/**
 * Format the RESPONSE `amount_charged` — the exact GROSS the company was actually charged for a
 * COMPANY_CARD send/batch (the face value + the forced cover-fees gross-up, e.g. "51.78" for a $50
 * card), a decimal wire STRING — for a post-send "Charged" surface (GC1-CMP1: surface the gross from
 * the response, never recomputed client-side). Returns `null` when the field is absent/blank: a
 * COMPANY_WALLET send debits exactly face (no charge/fees), and a batch-minted per-card row carries no
 * per-card charge (the batch is the charge grain) — those render FACE ONLY, never "$0". Exact cents via
 * string math, never a float / re-round.
 */
export function formatGiftCardCharged(amountCharged: string | null | undefined): string | null {
  const cents = parseCentsAmount(amountCharged ?? '');
  return cents === null ? null : formatCentsUsd(cents);
}

/** Display label for the RESPONSE funding enum (`GiftCardFunding`). Company sends are COMPANY_CARD /
 *  COMPANY_WALLET; other values are tolerated with a generic fallback (never throws on an unknown). */
export function fundingLabel(funding: GiftCardFunding | null | undefined): string {
  switch (funding) {
    case 'COMPANY_CARD':
      return 'Company card';
    case 'COMPANY_WALLET':
      return 'Company wallet';
    case 'CARD_PAYMENT':
      return 'Card';
    case 'WALLET':
      return 'Wallet';
    case 'ADMIN_ISSUED':
      return 'Admin issued';
    default:
      return '—';
  }
}

// ── Error classification ────────────────────────────────────────────────────────────────────────

/**
 * Whether a failed send is the backend's INSUFFICIENT-FUNDS signal — a company WALLET gift that exceeds
 * the wallet balance. The backend maps `InsufficientFundsException` to HTTP **409** with the stable
 * title "Insufficient funds" (errorhandling.py). A DEFINITE no-charge, surfaced as its own first-class
 * state distinct from a generic decline.
 */
export function isInsufficientFundsError(error: GivmoError): boolean {
  if (error.status !== 409) return false;
  return error.entries.some((e) => e.title === 'Insufficient funds');
}

/**
 * Whether a failed send is the employer VELOCITY-CAP signal (D-EP-6) — the per-company gift-card send
 * rate cap. The backend maps `RateLimitExceededException` to HTTP **429**. This cap rejects BEFORE any
 * charge/debit (a DEFINITE no-charge), so it is surfaced as its own first-class, NON-retryable state:
 * an immediate "try again" would just re-hit the cap, and there is no maybe-charged ambiguity to
 * acknowledge. (The shared `isRetryableChargeError` classifies 429 as retryable for the maybe-charged
 * general case; the dialog checks THIS predicate first so a velocity cap never enters that path.)
 */
export function isVelocityCapError(error: GivmoError): boolean {
  return error.status === 429;
}
